import { DateTime } from "luxon";
import moment from "moment";
import { UTC_TIMEZONE, DEFAULT_TIMEZONE } from "Helpers/Constants";
import { convertUtcTimestampToPstDate, convertUtcToPstTime } from "Hooks/commanUtility";

export function isBeforetwelveandsix(delivery_date_time) {
  let time = convertDateTime(delivery_date_time, "iso2", "hh:mm a");
  let beforTwelve = moment(time, "hh:mm a").isBefore(
    moment("02:00 PM", "hh:mm a")
  );
  let beforSix = moment(time, "hh:mm a").isBefore(
    moment("05:00 PM", "hh:mm a")
  );
  return { beforSix, beforTwelve };
}

/**
 * Converts date/time between different formats and timezones
 * @param {string} input - Input date/time value
 * @param {string} type - Conversion type (iso, seconds, format, etc.)
 * @param {string} format1 - Primary format for output
 * @param {string} format2 - Secondary format (used in some cases)
 * @param {string} timeZone1 - Source timezone (defaults to store timezone)
 * @param {string} timeZone2 - Target timezone (defaults to UTC)
 */
export function convertDateTime(input='', type, format1='', format2='', timeZone1='', timeZone2='') {
  // console.log("convertDateTime 1", "input: ", input, "type: ", type, "format1: ", format1, "format2: ", format2, "timeZone1: ", timeZone1, "timeZone2: ", timeZone2);
  // Set default timezones if not provided
  if(!timeZone1){
    timeZone1 = localStorage.getItem("storeTimezone") || DEFAULT_TIMEZONE;
  }
  if(!timeZone2){
    timeZone2 = UTC_TIMEZONE;
  }
  // console.log("convertDateTime 2", "timeZone1: ", timeZone1, "timeZone2: ", timeZone2);
  switch (type) {
    // ISO format conversions
    case 'iso':  // Convert ISO string to specified timezone and format
      return DateTime.fromISO(input).setZone(timeZone1).toFormat(format1);

    case 'iso2': // Convert ISO string from timeZone2 to timeZone1
      return DateTime.fromISO(input, { zone: timeZone2 }).setZone(timeZone1).toFormat(format1);

    case 'iso3': // Convert ISO string in timeZone2 to specified format
      return DateTime.fromISO(input, { zone: timeZone2 }).toFormat(format1);

    case 'iso4': // Simple ISO string to format conversion
      return DateTime.fromISO(input).toFormat(format1);

    // Date component extractions from ISO
    case 'iso5': // Get day of month
      return DateTime.fromISO(input).toJSDate().getDate();

    case 'iso6': // Get month (0-11)
      return DateTime.fromISO(input).toJSDate().getMonth();

    case 'iso7': // Get full year
      return DateTime.fromISO(input).toJSDate().getFullYear();

    // Unix timestamp (seconds) conversions
    case 'seconds': // Convert seconds to timeZone1 with format
      return DateTime.fromSeconds(input).setZone(timeZone1).toFormat(format1);
     
    case 'seconds2': // Convert seconds to timeZone1 locale string
      return DateTime.fromSeconds(input).setZone(timeZone1).toJSDate().toLocaleString("en-US", {timeZone: timeZone1});

    case 'seconds3': // Convert seconds from timeZone2 to timeZone1
      return DateTime.fromSeconds(input, { zone: timeZone2 }).setZone(timeZone1).toFormat(format1);

    case 'seconds4': // Convert seconds to timeZone2 with format
      return DateTime.fromSeconds(input).setZone(timeZone2).toFormat(format1);
      
    case 'seconds5': // Convert seconds to timeZone2 locale string
      return DateTime.fromSeconds(input).toJSDate().toLocaleString("en-US", {timeZone: timeZone2});

    case 'seconds6': // Convert seconds to JS Date object
      return DateTime.fromSeconds(input).toJSDate();

    case 'seconds7': // Convert seconds to previous day's JS Date
      return DateTime.fromSeconds(input).minus({ day: 1 }).toJSDate();

    // Format-based conversions
    case 'format': // Convert from format to timeZone1
      return DateTime.fromFormat(input).setZone(timeZone1).toFormat(format1);
    
    case 'format2': // Convert from format2/timeZone2 to timeZone1/format1
      return DateTime.fromFormat(input, format2, { zone: timeZone2 }).setZone(timeZone1).toFormat(format1);

    case 'format3': // Convert from format2/timeZone1 to timeZone2/format1
      return DateTime.fromFormat(input, format2, { zone: timeZone1 }).setZone(timeZone2).toFormat(format1);

    case 'format4': // Simple format conversion
      return DateTime.fromFormat(input, format2).toFormat(format1);

    // Format to seconds conversions
    case 'format5': // Convert format1/timeZone1 to timeZone2 seconds
      return DateTime.fromFormat(input, format1, { zone: timeZone1 }).setZone(timeZone2).toSeconds();

    case 'format6': // Convert format1/timeZone2 to timeZone1 seconds
      return DateTime.fromFormat(input, format1, { zone: timeZone2 }).setZone(timeZone1).toSeconds();

    // DateTime object conversions
    case 'format7': // Convert format1/timeZone2 to timeZone1 DateTime
      return DateTime.fromFormat(input, format1, { zone: timeZone2 }).setZone(timeZone1);

    case 'format7temp': // Convert format1/timeZone2 to timeZone1 DateTime
      return DateTime.fromFormat(input, format1, { zone: timeZone2})
      .plus({ days: 1 })  // Add one day to UTC time
      .setZone(timeZone1);

    case 'format8': // Convert format1/timeZone1 to timeZone2 DateTime
      return DateTime.fromFormat(input, format1, { zone: timeZone1 }).setZone(timeZone2);

    case 'format9': // Validate format1/timeZone2 in timeZone1 locale
      return DateTime.fromFormat(input, format1, { zone: timeZone2 }).setLocale(timeZone1).isValid;

    case 'format10': // Convert format2/timeZone2 to timeZone1/format1
      return DateTime.fromFormat(input, format2, { zone: timeZone2 }).setLocale(timeZone1).toFormat(format1);

    // JavaScript Date conversions
    case 'jsDate': // Convert JS Date to seconds
      return DateTime.fromJSDate(input).toSeconds();

    case 'local': // Get current local timestamp
      return DateTime.local().valueOf();

    case 'now': // Get current time in timeZone1 with format
      return DateTime.now().setZone(timeZone1).toFormat(format1);

    case 'moment': // Convert using moment.js
      return moment(input).tz(timeZone1).format(format1);

    default:
      throw new Error('Unsupported type');
  }
}

/**
 * Convert createdAt and updatedAt fields in an object to specified format
 * @param {Object} obj - Object containing createdAt and updatedAt fields
 * @param {string} outputFormat - Output format for datetime
 * @param {string} outputTimezone - Output timezone
 * @returns {Object} - Object with converted datetime fields
 */
const convertDateTimeFields = (obj) => {
  return {
    ...obj,
    createdAt: obj.createdAt ? convertDateTime(obj.createdAt, 'iso', "ccc LLL dd, yyyy, hh:mm a") : obj.createdAt,
    updatedAt: obj.updatedAt ? convertDateTime(obj.updatedAt, 'iso', "ccc LLL dd, yyyy, hh:mm a") : obj.updatedAt
  };
};

/**
 * Convert 24-hour time format to 12-hour format with AM/PM
 * @param {string} time24 - Time in 24-hour format (e.g., "19:00", "09:30")
 * @param {boolean} isConvertTimezone - Whether to convert the time to the local timezone
 * @returns {string} - Time in 12-hour format with AM/PM (e.g., "7:00 PM", "9:30 AM")
 * 
 * @example
 * convert24To12Hour("19:00") // Returns "7:00 PM"
 * convert24To12Hour("09:30") // Returns "9:30 AM"
 * convert24To12Hour("00:00") // Returns "12:00 AM"
 * convert24To12Hour("12:00") // Returns "12:00 PM"
 */
const convert24To12Hour = (time24, isConvertTimezone=false) => {
  if(isConvertTimezone){
    // Split the time string into hours and minutes components
    let [hours, minutes] = time24.split(':');

    // Pad single digit hours with leading zero for proper formatting
    if (hours.length === 1) hours = "0" + hours;

    // Convert to 24-hour format using moment.js and then format to 12-hour with AM/PM
    let twentyFourFormat = moment(hours + ":" + minutes, "HH:mm").format("hh:mm A");

    // Use the common convertDateTime function to handle timezone conversion
    let commonDateTimeConvert = convertDateTime(twentyFourFormat, "format7", "hh:mm a")

    // Return the final formatted time string with hours, minutes, and AM/PM indicator
    return `${commonDateTimeConvert.toFormat("hh")}:${commonDateTimeConvert.toFormat("mm")} ${commonDateTimeConvert.toFormat("a")}`;
  } else {
    // Split the time string into hours and minutes
    const [hours, minutes] = time24.split(':');

    // Convert hours string to integer
    const hour = parseInt(hours);

    // Determine AM/PM based on hour value (12 and above = PM, below 12 = AM)
    const ampm = hour >= 12 ? 'PM' : 'AM';

    // Convert to 12-hour format (0 becomes 12, 13 becomes 1, etc.)
    const hour12 = hour % 12 || 12;

    // Return formatted string with 12-hour time and AM/PM
    return `${hour12}:${minutes} ${ampm}`;
  }
}

/**
 * Convert audit trail details based on module type
 * This function handles datetime conversion for different modules in the audit trail
 * @param {Object} data - The data object containing audit trail information
 * @param {string} moduleName - The name of the module to process
 * @returns {Object} - The processed data with converted datetime fields
 */
export function convertAuditTrailDetails(data, moduleName="") {
  switch (moduleName) {
    case "blockout_dates":
      // Convert blockout date fields to timezone format
      if(data.start_blocking_on){
          data.start_blocking_on = convertUtcTimestampToPstDate(data.start_blocking_on,"ccc LLL dd, yyyy");
      }
      if(data.stop_repeating_on){
          data.stop_repeating_on = convertUtcTimestampToPstDate(data.stop_repeating_on, "ccc LLL dd, yyyy");
      }
      return data;

    case "categories":
      // Convert main category data and all nested arrays (banners, faqs, hierarchies)
      data = {
        ...convertDateTimeFields(data),
        banners: data?.banners?.map(banner => convertDateTimeFields(banner)),
        cms_faqs: data?.cms_faqs?.map(faq => convertDateTimeFields(faq)),
        category_hierarchies: data?.category_hierarchies?.map(cat => convertDateTimeFields(cat))
      };        
      return data;

    case "category_hierarchies":
      // No conversion needed for this module
      return data;

    case "cms_blogs":
      // Convert blog data including blog_date and all nested arrays
      data = {
        ...convertDateTimeFields(data),
        blog_date: data.blog_date ? moment(data.blog_date).format("MM-DD-YYYY") : data.blog_date,
        banners: data?.banners?.map(banner => convertDateTimeFields(banner)),
        categories: data?.categories?.map(cat => convertDateTimeFields(cat)),
        similar_blogs: data?.similar_blogs?.map(blog => convertDateTimeFields(blog)),
        seo: convertDateTimeFields(data?.seo)
      };  
      return data;

    case "cms_blog_categories":
      // No conversion needed for this module
      return data;

    case "cms_locations":
      // Convert location data with complex nested structure for category_hierarchy and seo
      data = {
        ...convertDateTimeFields(data),
        banners: data?.banners?.map(banner => convertDateTimeFields(banner)),
        cms_location_categories: data.cms_location_categories.map(category => ({
          ...category,
          // Convert category_hierarchy level datetime fields
          category_hierarchy: category.category_hierarchy ? {
            ...category.category_hierarchy,
            createdAt: category.category_hierarchy.createdAt ? 
              convertDateTime(category.category_hierarchy.createdAt, 'iso', "ccc LLL dd, yyyy, hh:mm a") : 
              category.category_hierarchy.createdAt,
            updatedAt: category.category_hierarchy.updatedAt ? 
              convertDateTime(category.category_hierarchy.updatedAt, 'iso', "ccc LLL dd, yyyy, hh:mm a") : 
              category.category_hierarchy.updatedAt,
            // Convert category_hierarchy.category level datetime fields
            category: category.category_hierarchy.category ? {
              ...category.category_hierarchy.category,
              createdAt: category.category_hierarchy.category.createdAt ? 
                convertDateTime(category.category_hierarchy.category.createdAt, 'iso', "ccc LLL dd, yyyy, hh:mm a") : 
                category.category_hierarchy.category.createdAt,
              updatedAt: category.category_hierarchy.category.updatedAt ? 
                convertDateTime(category.category_hierarchy.category.updatedAt, 'iso', "ccc LLL dd, yyyy, hh:mm a") : 
                category.category_hierarchy.category.updatedAt
            } : category.category_hierarchy.category
          } : category.category_hierarchy,
          // Convert seo level datetime fields
          seo: category.seo ? {
            ...category.seo,
            createdAt: category.seo.createdAt ? 
              convertDateTime(category.seo.createdAt, 'iso', "ccc LLL dd, yyyy, hh:mm a") : 
              category.seo.createdAt,
            updatedAt: category.seo.updatedAt ? 
              convertDateTime(category.seo.updatedAt, 'iso', "ccc LLL dd, yyyy, hh:mm a") : 
              category.seo.updatedAt
          } : category.seo,
          // Convert root level datetime fields
          createdAt: category.createdAt ? 
            convertDateTime(category.createdAt, 'iso', "ccc LLL dd, yyyy, hh:mm a") : 
            category.createdAt,
          updatedAt: category.updatedAt ? 
            convertDateTime(category.updatedAt, 'iso', "ccc LLL dd, yyyy, hh:mm a") : 
            category.updatedAt
        }))
      };
      return data;

    case "configurations":
      // No conversion needed for this module
      return data;

    case "coupons":
      // Convert coupon active date fields to PST format
      if(data.active_start_date){
        data.active_start_date = convertUtcTimestampToPstDate(data.active_start_date,"ccc LLL dd, yyyy, hh:mm a");
      }
      if(data.active_end_date){
          data.active_end_date = convertUtcTimestampToPstDate(data.active_end_date, "ccc LLL dd, yyyy, hh:mm a");
      }
      return data;

    case "delivery_matrix":
      // Convert delivery matrix time fields from UTC to PST
      data = {
        ...data,
        delivery_matrix_items: data?.delivery_matrix_items?.map(item => ({
          ...item,
          delivery_time: item.delivery_time ? convertUtcToPstTime(moment(item.delivery_time, "HH:mm:ss").unix()) : item.delivery_time,
          order_by_time: item.order_by_time ? convertUtcToPstTime(moment(item.order_by_time, "HH:mm:ss").unix()) : item.order_by_time
        }))
      };
      return data;

    case "exceptions":
      // No conversion needed for this module
      return data;

    case "faqs":
      // No conversion needed for this module
      return data;

    case "holiday_cutoffs":
      // Convert holiday cutoff date fields using seconds4 conversion
      if(data.start_date){
        data.start_date = convertDateTime(data.start_date, "seconds4", "ccc LLL dd, yyyy");
      }
      if(data.end_date){
          data.end_date = convertDateTime(data.end_date, "seconds4", "ccc LLL dd, yyyy");
      }
      return data;

    case "home_pages":
      // No conversion needed for this module
      return data;

    case "inventory_products":
      // Convert inventory product datetime fields
      data = {
        ...convertDateTimeFields(data)
      }; 
      return data;

    case "inventory_request_resources":
      // Convert inventory request resources with nested ingredient data
      data = {
        ...convertDateTimeFields(data),
        inventory_request_resource_ingredients: data?.inventory_request_resource_ingredients?.map(ingredient => ({
          ...convertDateTimeFields(ingredient), 
          destinationInventoryProduct: ingredient?.destinationInventoryProduct ? convertDateTimeFields(ingredient?.destinationInventoryProduct) : ingredient?.destinationInventoryProduct,
          sourceInventoryProduct: ingredient?.sourceInventoryProduct ? convertDateTimeFields(ingredient?.sourceInventoryProduct) : ingredient?.sourceInventoryProduct,
        }))
      }; 
      return data;

    case "inventory_waste_sheets":
      // Convert inventory waste sheets with nested ingredient data
      data = {
        ...convertDateTimeFields(data),
        inventory_waste_sheet_ingredients: data?.inventory_waste_sheet_ingredients?.map(ingredient => ({
          ...convertDateTimeFields(ingredient), 
          inventory_product: ingredient?.inventory_product ? convertDateTimeFields(ingredient?.inventory_product) : ingredient?.inventory_product,
        }))
      }; 
      return data;

    case "cms_pages":
      // No conversion needed for this module
      return data;

    case "payroll_dates":
      // No conversion needed for this module
      return data;

    case "product_recipe_raw_materials":
      // Convert product recipe raw materials datetime fields
      data = {
        ...convertDateTimeFields(data),
      };
      return data;

    case "product_recipe_raw_materials_price":
      // No conversion needed for this module
      return data;

    case "product_recipes":
      // Convert product recipes with nested ingredient data
      data = {
        ...convertDateTimeFields(data),
        product_recipe_ingredients: data?.product_recipe_ingredients?.map(ingredient => ({
          ...convertDateTimeFields(ingredient),
        }))
      };
      return data;

    case "custom_product_recipe":
      // Convert array of custom product recipes
      data = data?.map(recipe => ({
        ...convertDateTimeFields(recipe),
      }));
      return data;

    case "product_recipe_types":
      // Convert product recipe types datetime fields
      data = {
        ...convertDateTimeFields(data),
      };
      return data;

    case "product_recipe_units":
      // Convert product recipe units datetime fields
      data = {
        ...convertDateTimeFields(data),
      };
      return data;

    case "products":
      // No conversion needed for this module
      return data;

    case "product_sequence":
      // No conversion needed for this module
      return data;

    case "sale_product_sequence":
      // No conversion needed for this module
      return data;

    case "roles":
      // No conversion needed for this module
      return data;

    case "store_settings_email_notifications":
      // No conversion needed for this module
      return data;

    case "store_settings_rollover_times":
      // Convert rollover time from 24-hour to 12-hour format
      data = {
        ...data,
        value: convert24To12Hour(data.value)
      }
      return data;

    case "store_settings_upcharge_dates":
      // Convert upcharge date fields to PST format
      if(data.apllies_on){
        data.apllies_on = convertUtcTimestampToPstDate(data.apllies_on,"ccc LLL dd, yyyy");
      }
      if(data.apllies_untill){
          data.apllies_untill = convertUtcTimestampToPstDate(data.apllies_untill, "ccc LLL dd, yyyy");
      }
      return data;

    case "store_settings_zipcodes":
      // No conversion needed for this module
      return data;  

    case "store_settings_folders":
      // No conversion needed for this module
      return data;

    case "stores":
      // Convert store open/close times from 24-hour to 12-hour format
      data = {
        ...data,
        store_settings_openclose_times: data?.store_settings_openclose_times?.map(time => ({
          ...time,
          open_value: time.open_value ? convert24To12Hour(time.open_value, true) : time.open_value,
          close_value: time.close_value ? convert24To12Hour(time.close_value, true) : time.close_value
        }))
      }
      return data;

    case "users":
      // No conversion needed for this module
      return data;

    case "orders":
      // Convert order data with complex nested structure including recipients, products, validations, and notes
      data = {
        ...data,
        createdAt: data.createdAt ? convertDateTime(data.createdAt, 'iso', "ccc LLL dd, yyyy, hh:mm a") : data.createdAt,
        updatedAt: data.updatedAt ? convertDateTime(data.updatedAt, 'iso', "ccc LLL dd, yyyy, hh:mm a") : data.updatedAt,
        delivery_date_time: data.delivery_date_time ? convertDateTime(data.delivery_date_time, "iso", "ccc LLL dd, yyyy hh:mm a") : data.delivery_date_time,
        design_complete_time: data.design_complete_time ? convertDateTime(data.design_complete_time, 'iso', "ccc LLL dd, yyyy, hh:mm a") : data.design_complete_time,
        actual_order_delivered_time: data.actual_order_delivered_time ? convertDateTime(data.actual_order_delivered_time, 'iso', "ccc LLL dd, yyyy, hh:mm a") : data.actual_order_delivered_time,
        order_recipients: data?.order_recipients?.map(recipient => ({
          ...convertDateTimeFields(recipient),
          order_products: recipient?.order_products?.map(product => ({
            ...convertDateTimeFields(product),
          }))
        })),
        order_validations: data?.order_validations?.map(validation => ({
          ...convertDateTimeFields(validation),
        })),
        order_internal_notes: data?.order_internal_notes?.map(note => ({
          ...convertDateTimeFields(note),
        })),
      };        
      return data;

    case "stock_order":
      // Convert stock order datetime fields
      data = {
        ...convertDateTimeFields(data),
      };        
      return data;
    case "customers":
      // Convert customer datetime fields
      data = {
        ...convertDateTimeFields(data),
      };  
      return data;

    case "timeclock":
      // Convert timeclock datetime fields
      data = {
        ...convertDateTimeFields(data),
        change_requests: data?.change_requests?.map(request => ({
          ...convertDateTimeFields(request),
        })),
        clock_times: data?.clock_times?.map(time => ({
          ...convertDateTimeFields(time),
        })),
      };
      return data;

    default:
      // Return data unchanged if module type is not recognized
      return data;
  }
}